import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CreatableSelect from "react-select/creatable";
import useSWR from "swr";
import { URLS } from "../../../../services/urls";
import { Link, useParams } from "react-router-dom";
import { API } from "../../../../services/axios";
import { getUserDetails } from "../../../../components/utils/functions";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { styles } from "../../../../components/utils/reactSelect";
import { confirmDeletion } from "../../../../components/dletion";
import { ErrorMessage } from "@hookform/error-message";
import { useNavigate } from "react-router-dom";

export const ManageIntroduction = ({ nextStep, handleRefetch, onProfileCreated }) => {
  const [loading, setLoading]           = useState(false);
  const [markingAsDefault, setMarkingAsDefault] = useState(false);
  const [search, setSearch]             = useState("");
  const [cv, setCV]                     = useState();
  const [profile, setProfile]           = useState();
  const [selected, setSelected]         = useState([]);
  const [selectedProfession, setProfession] = useState(null);

  const { auth }  = useSelector((state) => state.auth);
  const { action } = useParams();
  const isCreate   = action === "create";
  const { t }      = useTranslation();
  const navigate   = useNavigate();

  const { data: skills,     isLoading: loadingSkills }     = useSWR(URLS.CONFIGS.PROFILE.SKILLS.list(search));
  const { data: professions, isLoading: loadingProfessions } = useSWR(URLS.CONFIGS.PROFILE.PREFESSION.list(search));

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({});

  // ─── CV file handlers ──────────────────────────────────────────────────────
  const handleChangeCV = (e) => setCV(e.target.files[0]);
  const handleDeleteCV = (e) => { e.preventDefault(); setCV(null); };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = (data) => {
    setLoading(true);
    const user = getUserDetails();

    const obj = {
      primary_role:     data.postTitle,
      bio:              data.userBio,
      salary_min:       data.minSalary,
      default:          !auth?.user?.profile?.id,
      user:             user?.user?.id,
      profession:       selectedProfession?.value,
      skills:           selected.map((sk) => sk.value),
      years_experience: data.yearExp,
    };

    if (isCreate) {
      // ── CREATE ──
      API.post(URLS.USERS.PROFILE.create, obj)
        .then((resp) => {
          const newId = resp.data.id;
          // Upload CV file if provided
          if (cv && cv.type) {
            const fd = new FormData();
            fd.append("cv", cv);
            API.patch(URLS.USERS.PROFILE.update(newId), fd).catch(() => {});
          }
          setLoading(false);
          toast.success(t("introEditedSuccessfuly"));
          // Let parent (ManageResume) handle navigation to edit mode
          if (onProfileCreated) onProfileCreated(newId);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
          setLoading(false);
        });
    } else {
      // ── UPDATE ──
      API.patch(URLS.USERS.PROFILE.update(action), obj)
        .then((resp) => {
          const newId = resp.data.id;
          // Upload CV file only if a new file was selected
          if (cv && cv.type) {
            const fd = new FormData();
            fd.append("cv", cv);
            API.patch(URLS.USERS.PROFILE.update(newId), fd).catch(() => {});
          }
          setLoading(false);
          toast.success(t("introEditedSuccessfuly"));
          handleRefetch();  // Refresh resume data in parent
          nextStep();        // Move to next step
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
          setLoading(false);
        });
    }
  };

  // ─── Skills helpers ────────────────────────────────────────────────────────
  const handleCreateSkill = (inputValue) => {
    API.post(URLS.CONFIGS.PROFILE.SKILLS.create, {
      name: inputValue,
      created_by: auth?.user?.id,
    }).then(({ data }) => {
      setSelected((prev) => [{ label: data.name, value: data.id }, ...prev]);
    });
  };

  const handleCreateProfession = (inputValue) => {
    API.post(URLS.CONFIGS.PROFILE.PREFESSION.create, {
      name: inputValue,
      description: `Profession created by user ${auth?.user?.email}`,
    }).then(({ data }) => {
      setProfession({ label: data.name, value: data.id });
    });
  };

  // ─── Mark as default ───────────────────────────────────────────────────────
  const handleMarkAsDefault = () => {
    setMarkingAsDefault(true);
    API.post(URLS.USERS.PROFILE.markAsDefault(action), {})
      .then(() => {
        setMarkingAsDefault(false);
        toast.success(t("textCvMarkedAsDefault"));
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.error ??
          err?.response?.data?.detail ??
          t("textCvMarkedAsDefaultError")
        );
        setMarkingAsDefault(false);
      });
  };

  // ─── Delete profile ────────────────────────────────────────────────────────
  const handleDeleteProfile = () => {
    const onSuccess = () => navigate("/dashboard/my-resumes");
    confirmDeletion(URLS.USERS.PROFILE.update(action), t, onSuccess);
  };

  // ─── Load existing data ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isCreate) {
      API.get(URLS.USERS.PROFILE.details(action))
        .then(({ data }) => {
          setValue("postTitle", data.primary_role);
          setValue("userBio",   data.bio);
          setValue("minSalary", data.salary_min);
          setValue("yearExp",   data.years_experience);
          setCV({ name: data.cv });
          setSelected(
            data?.skills?.map((i) => ({ label: i.name, value: i.id })) ?? []
          );
          setProfession(
            data?.profession
              ? { label: data.profession.name, value: data.profession.id }
              : null
          );
          setProfile(data);
        })
        .catch((err) => toast.error(err?.response?.data?.detail));
    }
  }, [action, isCreate]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Actions bar (edit mode only) */}
      {!isCreate && (
        <div className="panel-white mb-30">
          <div className="box-padding">
            <div className="d-flex justify-content-end">
              {!profile?.default && (
                <button className="btn-primary btn" onClick={handleMarkAsDefault}>
                  {t("markCvDefault")}
                  {markingAsDefault && (
                    <span className="spinner-border spinner-border-sm ml-10" role="status" />
                  )}
                </button>
              )}
              <button className="btn text-error ml-15" onClick={handleDeleteProfile}>
                {t("deleteButtonText")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="panel-white mb-30">
        <div className="box-padding">
          <h5>{t("updateYourCV")}</h5>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row mt-30">

              {/* Upload CV file */}
              <div className="col-lg-6 col-md-6">
                <div className="form-group mb-30">
                  <div className="box-upload">
                    <div className="add-file-upload">
                      <input
                        className="fileupload"
                        type="file"
                        name="file"
                        id="uploadCv"
                        onChange={handleChangeCV}
                      />
                    </div>
                    <label htmlFor="uploadCv" className="btn btn-default">
                      {t("uploadFile")} <span>*</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 col-md-6">
                {cv && (
                  <div className="form-group mb-30 box-file-uploaded">
                    <span>{cv?.name}</span><br />
                    <Link className="btn btn-delete" to="#" onClick={handleDeleteCV}>
                      {t("buttonDelete")}
                    </Link>
                  </div>
                )}
              </div>

              {/* Primary role */}
              <div className="col-lg-6 col-md-12">
                <div className="form-group mb-30">
                  <label className="font-sm color-text-mutted mb-10" htmlFor="postTitle">
                    {t("specialities")} <span className="text-primary">*</span>
                  </label>
                  <input
                    {...register("postTitle", { required: t("textFormFieldRequired") })}
                    id="postTitle"
                    className="form-control"
                    type="text"
                  />
                  <ErrorMessage errors={errors} name="postTitle"
                    render={({ message }) => <span className="text-primary">{message}</span>}
                  />
                </div>
              </div>

              {/* Profession */}
              <div className="col-lg-6 col-md-12">
                <div className="form-group mb-30">
                  <label className="font-sm color-text-mutted mb-10">
                    {t("profession")} <span className="text-primary">*</span>
                  </label>
                  <CreatableSelect
                    options={professions?.results?.map((i) => ({ label: i.name, value: i.id })) ?? []}
                    isClearable
                    isSearchable
                    inputId="select-profession"
                    styles={styles}
                    isLoading={loadingProfessions}
                    value={selectedProfession}
                    onCreateOption={handleCreateProfession}
                    onChange={(e) => setProfession(e)}
                  />
                </div>
              </div>

              {/* Years of experience */}
              <div className="col-lg-6 col-md-6">
                <div className="form-group mb-30">
                  <label className="font-sm color-text-mutted mb-10">
                    {t("yearOfExperience")}
                  </label>
                  <input
                    {...register("yearExp", { required: t("textFormFieldRequired") })}
                    className="form-control"
                    type="number"
                    min={0}
                    placeholder="5"
                  />
                </div>
              </div>

              {/* Skills */}
              <div className="col-lg-6 col-md-12">
                <div className="form-group mb-30">
                  <label className="font-sm color-text-mutted mb-10">
                    {t("profileSkills")} <span className="text-primary">*</span>
                  </label>
                  <CreatableSelect
                    options={skills?.results?.map((i) => ({ label: i.name, value: i.id })) ?? []}
                    isClearable
                    isSearchable
                    closeMenuOnSelect={false}
                    isMulti
                    inputId="select-skills"
                    styles={styles}
                    isLoading={loadingSkills}
                    value={selected}
                    onCreateOption={handleCreateSkill}
                    onChange={(v) => setSelected(v)}
                  />
                </div>
              </div>

              {/* Minimum salary */}
              <div className="col-lg-6 col-md-6">
                <div className="form-group mb-30">
                  <label className="font-sm color-text-mutted mb-10">
                    {t("minSalary")} <span className="text-primary">*</span>
                  </label>
                  <input
                    {...register("minSalary", { required: t("textFormFieldRequired") })}
                    className="form-control"
                    type="number"
                    placeholder="0"
                  />
                  <ErrorMessage errors={errors} name="minSalary"
                    render={({ message }) => <span className="text-primary">{message}</span>}
                  />
                </div>
              </div>

              {/* Bio */}
              <div className="col-lg-12 col-md-12">
                <div className="form-group mb-30">
                  <label className="font-sm color-text-mutted mb-10">
                    {t("userBio")} <span className="text-primary">*</span>
                  </label>
                  <textarea
                    {...register("userBio", { required: t("textFormFieldRequired") })}
                    rows={4}
                    className="form-control"
                  />
                  <ErrorMessage errors={errors} name="userBio"
                    render={({ message }) => <span className="text-primary">{message}</span>}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="col-lg-12 col-md-12">
                <div className="d-flex">
                  <button className="btn btn-default btn-brand" type="submit">
                    {isCreate ? t("btnContinue") : t("btnUpdate")}
                    {loading && (
                      <span className="spinner-border spinner-border-sm ml-10" role="status" />
                    )}
                  </button>
                  {!isCreate && (
                    <button
                      onClick={nextStep}
                      className="btn btn-default btn-brand ml-auto"
                      type="button"
                    >
                      {t("textNext")}
                    </button>
                  )}
                </div>
              </div>

            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ManageIntroduction;
